import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaLightbulb, FaRocket, FaHandshake } from 'react-icons/fa';

const About = () => {
  const values = [
    {
      id: 1,
      title: 'Innovation',
      description: 'We embrace cutting-edge technologies and creative thinking to deliver innovative solutions.',
      icon: <FaLightbulb className="text-primary-500" size={30} />,
    },
    {
      id: 2,
      title: 'Excellence',
      description: 'We strive for excellence in everything we do, from code quality to client communication.',
      icon: <FaRocket className="text-primary-500" size={30} />,
    },
    {
      id: 3,
      title: 'Integrity',
      description: 'We build relationships based on trust, transparency, and ethical business practices.',
      icon: <FaHandshake className="text-primary-500" size={30} />,
    },
  ];

  return (
    <section id="about" className="section">
      <div className="container">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, type: "spring", damping: 15 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="relative">
              <motion.div
                className="w-full h-[400px] rounded-lg overflow-hidden shadow-xl"
                whileHover={{ scale: 1.02, boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)" }}
                transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
              >
                <motion.img
                  src="https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                  alt="Team collaboration"
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.1 }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.5 }}
                />
              </motion.div>
              <motion.div
                className="absolute -bottom-6 -right-6 w-40 h-40 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg z-[-1] shadow-lg"
                initial={{ opacity: 0, scale: 0.8, rotate: -5 }}
                whileInView={{ opacity: 0.9, scale: 1, rotate: 0 }}
                transition={{ duration: 0.5, delay: 0.3, type: "spring" }}
                viewport={{ once: true }}
              />
              <motion.div
                className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-tr from-secondary-300 to-secondary-100 rounded-lg z-[-1] shadow-md"
                initial={{ opacity: 0, scale: 0.8, rotate: 5 }}
                whileInView={{ opacity: 0.8, scale: 1, rotate: 0 }}
                transition={{ duration: 0.5, delay: 0.5, type: "spring" }}
                viewport={{ once: true }}
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="text-3xl font-bold mb-6">
              <motion.span
                className="inline-block"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                About
              </motion.span>{" "}
              <motion.span
                className="inline-block text-primary-600"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
              >
                FutureSoft
              </motion.span>
            </h2>

            <motion.p
              className="text-lg text-secondary-600 mb-6"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              viewport={{ once: true }}
            >
              FutureSoft Technologies is a leading software development company dedicated to creating innovative solutions that help businesses thrive in the digital age.
            </motion.p>

            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              viewport={{ once: true }}
            >
              {values.map((value) => (
                <div key={value.id} className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                    {value.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">{value.title}</h3>
                    <p className="text-secondary-600">{value.description}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1 }}
              viewport={{ once: true }}
            >
              <Link to="/about" className="btn btn-primary">
                Learn More About Us
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
